import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ActivityPanel } from "../activities/ActivityPanel";
import {
  parseActivityRoute,
  serializeActivityRoute,
  withoutActivityRoute,
} from "../activities/activityRoute";
import type { GuideOverlayState, GuidedDrawingActivity } from "../activities/activityTypes";
import {
  GuideSessionController,
  type GuideSessionState,
} from "../activities/GuideSessionController";
import { exportProjectBackup } from "../backup/exportProject";
import { CanvasStage } from "../canvas/CanvasStage";
import type {
  CanvasHistoryState,
  CanvasSaveArtifacts,
  CanvasStageHandle,
  DocumentChangeReason,
  DrawingSettings,
  DrawingTool,
  LayerPreviewBlob,
  RasterSelectionUiState,
} from "../canvas/canvasTypes";
import { Brand } from "../components/Brand";
import { BubbleInspector } from "../components/BubbleInspector/BubbleInspector";
import { BubbleTextDialog } from "../components/BubbleTextDialog/BubbleTextDialog";
import { ComicActionDialog } from "../components/ComicActionDialog/ComicActionDialog";
import { ComicToolbar } from "../components/ComicToolbar/ComicToolbar";
import { HistoryControls } from "../components/HistoryControls/HistoryControls";
import { LeaveWithoutSavingDialog } from "../components/LeaveWithoutSavingDialog";
import { ManualSaveButton } from "../components/ManualSaveButton/ManualSaveButton";
import { LayersPanel } from "../components/LayersPanel/LayersPanel";
import { RecoveryNotice } from "../components/RecoveryNotice/RecoveryNotice";
import { SelectionInspector } from "../components/SelectionInspector/SelectionInspector";
import { SaveIndicator } from "../components/SaveIndicator";
import { ToolDock } from "../components/ToolDock/ToolDock";
import { saveArtworkAtomically } from "../persistence/artworkRepository";
import type { ArtworkRecoveryResult } from "../persistence/recovery";
import { recoverArtwork } from "../persistence/recovery";
import { SaveCoordinator, type PersistenceStatus } from "../persistence/SaveCoordinator";
import type { PerfectDrawProject } from "../projects/types";
import type { ComicBubbleObject, ComicSelectionState, StudioTool } from "../comic/comicTypes";
import { isDrawingStudioTool } from "../comic/comicTypes";
import { applyDocumentStateToProject } from "../projects/projectMigration";
import { getProject } from "../storage/db";
import type { LayerControllerState } from "../layers/LayerController";
import type { LayerPreset } from "../layers/layerTypes";
import { LayerPreviewCoordinator } from "../layers/LayerPreviewCoordinator";
import { EMPTY_RASTER_SELECTION_UI_STATE } from "../selection/selectionTypes";
import type { ShapeMode } from "../shapes/shapeTypes";
import type { FillTolerance } from "../fill/fillTypes";
import { useParentMode } from "../parent/ParentModeContext";
import {
  isStudioToolAllowed,
  settleStudioPolicyChange,
  shouldRevokeFullToolAccess,
} from "../parent/permissionPolicy";

const EMPTY_HISTORY: CanvasHistoryState = {
  canUndo: false,
  canRedo: false,
  commandCount: 0,
  pointCount: 0,
  estimatedBytes: 0,
  wasTrimmed: false,
};

type DisplaySaveStatus = PersistenceStatus | "offline" | "recovered";

interface GuideRuntime {
  key: string | null;
  controller: GuideSessionController | null;
  session: GuideSessionState | null;
}

function createGuideRuntime(
  projectId: string | undefined,
  activity: GuidedDrawingActivity | null,
  initialStep: number,
): GuideRuntime {
  if (!projectId || !activity) return { key: null, controller: null, session: null };
  const controller = new GuideSessionController({
    projectId,
    activityId: activity.id,
    totalSteps: activity.steps.length,
    initialStep,
  });
  return {
    key: `${projectId}:${activity.id}`,
    controller,
    session: controller.getState(),
  };
}

function layerPixelsMayHaveChanged(reason: DocumentChangeReason): boolean {
  return (
    reason === "stroke" ||
    reason === "undo" ||
    reason === "redo" ||
    reason === "addComicObject" ||
    reason === "transformComicObject" ||
    reason === "editComicText" ||
    reason === "deleteComicObject" ||
    reason === "clearPanel" ||
    reason === "duplicatePanel" ||
    reason === "clearLayer" ||
    reason === "mergeLayerDown" ||
    reason === "rasterSelection" ||
    reason === "shape" ||
    reason === "fill"
  );
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

export default function StudioScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const parent = useParentMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const stageRef = useRef<CanvasStageHandle>(null);
  const coordinatorRef = useRef<SaveCoordinator<CanvasSaveArtifacts> | null>(null);
  const preservePreviousRef = useRef(false);
  const textDraftRef = useRef("");
  const textOriginalRef = useRef("");
  const textObjectIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const layerPreviewUrlsRef = useRef<Record<string, string>>({});
  const layerIdsRef = useRef(new Set<string>());
  const layerPreviewCoordinatorRef = useRef<LayerPreviewCoordinator<LayerPreviewBlob[]> | null>(
    null,
  );
  const [project, setProject] = useState<PerfectDrawProject | null>(null);
  const [recovery, setRecovery] = useState<ArtworkRecoveryResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStageReady, setIsStageReady] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [saveStatus, setSaveStatus] = useState<DisplaySaveStatus>("unsaved");
  const [history, setHistory] = useState<CanvasHistoryState>(EMPTY_HISTORY);
  const [tool, setTool] = useState<DrawingTool>("pencil");
  const [studioTool, setStudioTool] = useState<StudioTool>("pencil");
  const [colour, setColour] = useState("#111827");
  const [size, setSize] = useState(12);
  const [opacity, setOpacity] = useState(1);
  const [shapeMode, setShapeMode] = useState<ShapeMode>("outline");
  const [fillTolerance, setFillTolerance] = useState<FillTolerance>(24);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [comicSelection, setComicSelection] = useState<ComicSelectionState>({
    panelId: null,
    object: null,
  });
  const [rasterSelection, setRasterSelection] = useState<RasterSelectionUiState>({
    ...EMPTY_RASTER_SELECTION_UI_STATE,
  });
  const [textDraft, setTextDraft] = useState("");
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [showClearPanelDialog, setShowClearPanelDialog] = useState(false);
  const [showDuplicatePanelDialog, setShowDuplicatePanelDialog] = useState(false);
  const [duplicateTargetId, setDuplicateTargetId] = useState("");
  const [layerState, setLayerState] = useState<LayerControllerState | null>(null);
  const [layerError, setLayerError] = useState("");
  const [layerPreviewUrls, setLayerPreviewUrls] = useState<Record<string, string>>({});
  const [pendingLayerAction, setPendingLayerAction] = useState<{
    kind: "delete" | "clear" | "merge";
    layerId: string;
  } | null>(null);
  const previousParentPolicyRef = useRef({
    toolLevel: parent.toolLevel,
    destructive: parent.can("destructiveEdit"),
    parentUnlocked: parent.isUnlocked,
  });
  const canDestructiveEdit = parent.can("destructiveEdit");
  const canExportBackup = parent.can("exportBackup");
  const canUseActivities = parent.can("useActivities");
  const canAtActionBoundary = parent.can;
  const askParent = parent.askParent;
  const activityRoute = useMemo(() => parseActivityRoute(searchParams), [searchParams]);
  const hasActivityQuery = searchParams.has("activity") || searchParams.has("step");
  const routedActivity =
    canUseActivities && activityRoute.kind === "activity" ? activityRoute.activity : null;
  const guidedActivity: GuidedDrawingActivity | null =
    routedActivity?.type === "guided" ? routedActivity : null;
  const guideKey = guidedActivity && projectId ? `${projectId}:${guidedActivity.id}` : null;
  const initialGuideStep = activityRoute.kind === "activity" ? activityRoute.step : 1;
  const [guideRuntime, setGuideRuntime] = useState<GuideRuntime>(() =>
    createGuideRuntime(projectId, guidedActivity, initialGuideStep),
  );
  if (guideRuntime.key !== guideKey) {
    setGuideRuntime(createGuideRuntime(projectId, guidedActivity, initialGuideStep));
  }
  const guideController = guideRuntime.controller;
  const activeGuideSession = guideRuntime.session;
  const setGuideSession = useCallback((session: GuideSessionState | null): void => {
    setGuideRuntime((current) => ({ ...current, session }));
  }, []);

  useEffect(() => {
    if (
      canUseActivities &&
      activityRoute.kind === "activity" &&
      activityRoute.stepWasClamped &&
      searchParams.toString() !== activityRoute.canonicalSearch
    ) {
      setSearchParams(activityRoute.canonicalSearch, { replace: true });
    }
  }, [activityRoute, canUseActivities, searchParams, setSearchParams]);

  useEffect(() => {
    if (canUseActivities || !hasActivityQuery) return;
    const nextSearch = withoutActivityRoute(searchParams);
    if (nextSearch.toString() !== searchParams.toString()) {
      setSearchParams(nextSearch, { replace: true });
    }
    askParent("Ask a parent to open Drawing Activities. This project opened as a normal drawing.");
  }, [askParent, canUseActivities, hasActivityQuery, searchParams, setSearchParams]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const coordinator = new LayerPreviewCoordinator(
      async () => {
        const stage = stageRef.current;
        if (!stage) return [];
        return stage.exportLayerPreviews();
      },
      (previews) => {
        if (!mountedRef.current) return;
        const validLayerIds = layerIdsRef.current;
        const next = Object.fromEntries(
          previews
            .filter((preview) => preview.blob && validLayerIds.has(preview.layerId))
            .map((preview) => [preview.layerId, URL.createObjectURL(preview.blob!)]),
        );
        const previous = layerPreviewUrlsRef.current;
        layerPreviewUrlsRef.current = next;
        setLayerPreviewUrls(next);
        for (const url of Object.values(previous)) URL.revokeObjectURL(url);
      },
    );
    layerPreviewCoordinatorRef.current = coordinator;
    return () => {
      coordinator.dispose();
      if (layerPreviewCoordinatorRef.current === coordinator) {
        layerPreviewCoordinatorRef.current = null;
      }
    };
  }, []);

  useEffect(
    () => () => {
      for (const url of Object.values(layerPreviewUrlsRef.current)) URL.revokeObjectURL(url);
      layerPreviewUrlsRef.current = {};
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    const loadProject = async (): Promise<void> => {
      setIsLoading(true);
      setError("");
      setProject(null);
      setRecovery(null);
      if (!projectId) {
        setError("This project link is incomplete.");
        setIsLoading(false);
        return;
      }

      try {
        const storedProject = await getProject(projectId);
        if (cancelled) return;
        if (!storedProject) {
          setError("We couldn’t find that project on this device.");
          return;
        }
        const restored = await recoverArtwork(storedProject);
        if (cancelled) return;
        setProject(storedProject);
        setRecovery(restored);
        if (restored.kind === "corrupt") {
          setSaveStatus("failed");
        } else if (restored.kind === "recovered") {
          setSaveStatus("recovered");
        } else if (restored.kind === "current") {
          setSaveStatus(navigator.onLine ? "saved" : "offline");
        } else {
          setSaveStatus("unsaved");
        }
      } catch {
        if (!cancelled) setError("We couldn’t open this project. Please return to your library.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadProject();
    return () => {
      cancelled = true;
    };
  }, [loadAttempt, projectId]);

  useEffect(() => {
    const handleOnline = (): void => {
      setIsOnline(true);
      if (coordinatorRef.current?.getStatus() === "saved") setSaveStatus("saved");
    };
    const handleOffline = (): void => {
      setIsOnline(false);
      if (coordinatorRef.current?.getStatus() === "saved") setSaveStatus("offline");
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const handleCanvasReady = useCallback((): void => {
    if (!project || !recovery || coordinatorRef.current) return;
    const recovered = recovery.kind === "recovered";
    const needsLayerMigration =
      recovery.kind === "current" &&
      (recovery.record.schemaVersion < 3 || !recovery.record.currentLayers);
    preservePreviousRef.current = recovered;
    const initialGeneration =
      (recovery.kind === "current" || recovery.kind === "recovered" || recovery.kind === "corrupt"
        ? recovery.record.sourceGeneration
        : 0) + (recovered || needsLayerMigration ? 1 : 0);
    const coordinator = new SaveCoordinator<CanvasSaveArtifacts>({
      initialGeneration,
      initiallyDirty: recovery.kind === "blank" || recovered || needsLayerMigration,
      prepare: async () => {
        const stage = stageRef.current;
        if (!stage) throw new Error("The drawing canvas is not ready.");
        return stage.exportSaveArtifacts();
      },
      persist: async (artifacts, generation) => {
        const savedAt = new Date().toISOString();
        await saveArtworkAtomically({
          projectId: project.id,
          sourceGeneration: generation,
          savedAt,
          currentLayers: artifacts.rasterLayers,
          thumbnailBlob: artifacts.thumbnailBlob,
          currentDocumentState: artifacts.documentState,
          preservePreviousBlob: preservePreviousRef.current,
        });
        stageRef.current?.acknowledgeSavedLayers(artifacts.rasterLayers);
        preservePreviousRef.current = false;
        if (mountedRef.current) {
          setProject((current) =>
            current
              ? {
                  ...applyDocumentStateToProject(current, artifacts.documentState),
                  updatedAt: savedAt,
                  thumbnailBlob: artifacts.thumbnailBlob,
                }
              : current,
          );
        }
      },
      onStatusChange: (status) => {
        if (!mountedRef.current) return;
        setSaveStatus(status === "saved" && !navigator.onLine ? "offline" : status);
        if (status !== "failed") setSaveError("");
      },
    });
    coordinatorRef.current = coordinator;
    setIsStageReady(true);
    layerPreviewCoordinatorRef.current?.requestImmediately();
    if (needsLayerMigration) void coordinator.flush().catch(() => undefined);
  }, [project, recovery]);

  const handleDocumentChange = useCallback((reason: DocumentChangeReason): void => {
    coordinatorRef.current?.markDirty();
    if (layerPixelsMayHaveChanged(reason)) layerPreviewCoordinatorRef.current?.request();
  }, []);

  const handleLayerChange = useCallback((state: LayerControllerState): void => {
    const validLayerIds = new Set(state.layers.map((layer) => layer.id));
    layerIdsRef.current = validLayerIds;
    const previous = layerPreviewUrlsRef.current;
    const next = Object.fromEntries(
      Object.entries(previous).filter(([layerId]) => validLayerIds.has(layerId)),
    );
    if (Object.keys(next).length !== Object.keys(previous).length) {
      for (const [layerId, url] of Object.entries(previous)) {
        if (!validLayerIds.has(layerId)) URL.revokeObjectURL(url);
      }
      layerPreviewUrlsRef.current = next;
      setLayerPreviewUrls(next);
    }
    setLayerState(state);
  }, []);

  const openTextEditor = useCallback(
    (object: ComicBubbleObject): void => {
      if (!canAtActionBoundary("useFullTools")) return;
      textObjectIdRef.current = object.id;
      textDraftRef.current = object.data.text;
      textOriginalRef.current = object.data.text;
      setTextDraft(object.data.text);
      setShowTextEditor(true);
    },
    [canAtActionBoundary],
  );

  const closeTextEditor = useCallback((): void => {
    textObjectIdRef.current = null;
    textDraftRef.current = "";
    textOriginalRef.current = "";
    if (mountedRef.current) {
      setTextDraft("");
      setShowTextEditor(false);
    }
  }, []);

  const commitTextEditor = useCallback((): void => {
    if (canAtActionBoundary("useFullTools") && textObjectIdRef.current) {
      stageRef.current?.updateSelectedComicText(textDraftRef.current);
    }
    closeTextEditor();
  }, [canAtActionBoundary, closeTextEditor]);

  const flushLatestSave = useCallback(async (): Promise<void> => {
    commitTextEditor();
    stageRef.current?.finishActiveInteraction();
    const coordinator = coordinatorRef.current;
    if (!coordinator) throw new Error("The drawing is still opening.");
    try {
      await coordinator.flush();
    } catch {
      if (mountedRef.current)
        setSaveError("Your drawing is still here, but it could not be saved. Please retry.");
      throw new Error("The drawing could not be saved.");
    }
  }, [commitTextEditor]);

  useEffect(() => {
    const bestEffortFlush = (reason: "visibility-hidden" | "pagehide" | "studio-cleanup"): void => {
      closeTextEditor();
      stageRef.current?.interruptActiveInteraction(reason);
      void coordinatorRef.current?.flush().catch(() => undefined);
    };
    const handleVisibility = (): void => {
      if (document.visibilityState === "hidden") bestEffortFlush("visibility-hidden");
    };
    const handlePageHide = (): void => bestEffortFlush("pagehide");
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibility);
      bestEffortFlush("studio-cleanup");
      const coordinator = coordinatorRef.current;
      if (coordinator) {
        const pending = coordinator.flush();
        coordinator.dispose();
        void pending.catch(() => undefined);
      }
      coordinatorRef.current = null;
    };
  }, [closeTextEditor, projectId]);

  const undo = useCallback((): void => {
    stageRef.current?.undo();
  }, []);
  const redo = useCallback((): void => {
    stageRef.current?.redo();
  }, []);

  useEffect(() => {
    const handleShortcut = (keyboardEvent: KeyboardEvent): void => {
      if (isTextEditingTarget(keyboardEvent.target)) return;
      const key = keyboardEvent.key.toLowerCase();
      const modified = keyboardEvent.ctrlKey || keyboardEvent.metaKey;
      if (modified && key === "z" && keyboardEvent.shiftKey) {
        keyboardEvent.preventDefault();
        redo();
      } else if (modified && key === "z") {
        keyboardEvent.preventDefault();
        undo();
      } else if (modified && key === "y") {
        keyboardEvent.preventDefault();
        redo();
      } else if (
        (key === "delete" || key === "backspace") &&
        studioTool === "selectRasterArt" &&
        rasterSelection.hasSelection
      ) {
        keyboardEvent.preventDefault();
        if (canDestructiveEdit) stageRef.current?.deleteRasterSelection();
        else setLayerError("Ask a parent before deleting selected art.");
      } else if (
        (key === "delete" || key === "backspace") &&
        comicSelection.object &&
        canAtActionBoundary("useFullTools")
      ) {
        keyboardEvent.preventDefault();
        if (canDestructiveEdit) stageRef.current?.deleteSelectedComicObject();
        else setLayerError("Ask a parent before deleting dialogue.");
      } else if (
        key === "enter" &&
        studioTool === "selectRasterArt" &&
        rasterSelection.isTransforming
      ) {
        keyboardEvent.preventDefault();
        stageRef.current?.applyRasterSelection();
      } else if (key === "enter" && comicSelection.object && canAtActionBoundary("useFullTools")) {
        keyboardEvent.preventDefault();
        openTextEditor(comicSelection.object);
      } else if (key === "escape") {
        stageRef.current?.cancelActiveInteraction();
      } else if (
        studioTool === "selectRasterArt" &&
        rasterSelection.hasSelection &&
        key.startsWith("arrow")
      ) {
        keyboardEvent.preventDefault();
        const direction = key.replace("arrow", "") as "left" | "right" | "up" | "down";
        stageRef.current?.transformRasterSelection(
          `nudge-${direction}${keyboardEvent.shiftKey ? "-large" : ""}`,
        );
      } else if (
        comicSelection.object &&
        key.startsWith("arrow") &&
        canAtActionBoundary("useFullTools")
      ) {
        keyboardEvent.preventDefault();
        const direction = key.replace("arrow", "") as "left" | "right" | "up" | "down";
        stageRef.current?.transformSelectedComicObject(
          `nudge-${direction}${keyboardEvent.shiftKey ? "-large" : ""}`,
        );
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [
    canDestructiveEdit,
    canAtActionBoundary,
    comicSelection.object,
    openTextEditor,
    rasterSelection,
    redo,
    studioTool,
    undo,
  ]);

  useEffect(() => {
    const previous = previousParentPolicyRef.current;
    const next = {
      toolLevel: parent.toolLevel,
      destructive: canDestructiveEdit,
      parentUnlocked: parent.isUnlocked,
    };
    const settlement = settleStudioPolicyChange(previous, next, studioTool);
    const revokeFullAccess = shouldRevokeFullToolAccess(previous, next);
    if (revokeFullAccess) {
      stageRef.current?.revokeFullToolAccess();
      closeTextEditor();
      setComicSelection({ panelId: null, object: null });
      setRasterSelection({ ...EMPTY_RASTER_SELECTION_UI_STATE });
      setShowClearPanelDialog(false);
      setShowDuplicatePanelDialog(false);
      setDuplicateTargetId("");
      setPendingLayerAction(null);
    }
    if (settlement.shouldCancel && (!revokeFullAccess || !isDrawingStudioTool(studioTool))) {
      stageRef.current?.cancelActiveInteraction();
      setShowClearPanelDialog(false);
      setPendingLayerAction(null);
    }
    const fallbackTimer =
      (revokeFullAccess ? "pencil" : settlement.nextTool) !== studioTool
        ? window.setTimeout(() => {
            const nextTool = revokeFullAccess ? "pencil" : settlement.nextTool;
            setStudioTool(nextTool);
            if (nextTool === "pencil") setTool("pencil");
          }, 0)
        : null;
    previousParentPolicyRef.current = next;
    return () => {
      if (fallbackTimer !== null) window.clearTimeout(fallbackTimer);
    };
  }, [canDestructiveEdit, closeTextEditor, parent.isUnlocked, parent.toolLevel, studioTool]);

  const handleSave = async (): Promise<void> => {
    try {
      await flushLatestSave();
    } catch {
      // The visible status and retry button communicate the recoverable failure.
    }
  };

  const handleHome = async (): Promise<void> => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      await flushLatestSave();
      void navigate("/");
    } catch {
      setShowLeaveDialog(true);
      setIsNavigating(false);
    }
  };

  const handleExport = async (): Promise<void> => {
    if (!project || isExporting) return;
    if (!canExportBackup) {
      setSaveError("Ask a parent before exporting a project backup.");
      parent.askParent("Ask a parent before exporting a project backup.");
      return;
    }
    setIsExporting(true);
    try {
      await exportProjectBackup(project, flushLatestSave);
    } catch {
      setSaveError("The backup could not be created. Save the drawing and try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const updateGuideRoute = useCallback(
    (state: GuideSessionState): void => {
      setGuideSession(state);
      if (guidedActivity && !state.exited) {
        setSearchParams(serializeActivityRoute(guidedActivity, state.step), { replace: true });
      }
    },
    [guidedActivity, setGuideSession, setSearchParams],
  );

  const exitActivity = useCallback((): void => {
    stageRef.current?.cancelActiveInteraction();
    guideController?.exit();
    setGuideSession(null);
    setSearchParams(withoutActivityRoute(searchParams), { replace: true });
  }, [guideController, searchParams, setGuideSession, setSearchParams]);

  const activityPrevious = useCallback((): void => {
    stageRef.current?.cancelActiveInteraction();
    if (guideController) updateGuideRoute(guideController.previous());
  }, [guideController, updateGuideRoute]);

  const activityNext = useCallback((): void => {
    stageRef.current?.cancelActiveInteraction();
    if (!guideController) return;
    const next = guideController.next();
    if (next.exited) exitActivity();
    else updateGuideRoute(next);
  }, [exitActivity, guideController, updateGuideRoute]);

  const restartActivity = useCallback((): void => {
    stageRef.current?.cancelActiveInteraction();
    if (guideController) updateGuideRoute(guideController.restart());
  }, [guideController, updateGuideRoute]);

  const toggleGuide = useCallback((): void => {
    stageRef.current?.cancelActiveInteraction();
    if (guideController) setGuideSession(guideController.toggleGuide());
  }, [guideController, setGuideSession]);

  const guideOverlay = useMemo<GuideOverlayState | null>(
    () =>
      guidedActivity && activeGuideSession && !activeGuideSession.exited
        ? {
            activity: guidedActivity,
            currentStep: activeGuideSession.step,
            visible: activeGuideSession.visible,
          }
        : null,
    [activeGuideSession, guidedActivity],
  );

  if (isLoading) {
    return (
      <main className="screen-loader" aria-live="polite">
        <span className="spinner" aria-hidden="true" />
        <p>Opening your studio…</p>
      </main>
    );
  }

  if (error || !project || !recovery) {
    return (
      <main className="not-found-screen">
        <Brand compact />
        <section className="not-found-card">
          <span aria-hidden="true">?</span>
          <h1>Project not found</h1>
          <p>{error}</p>
          <div className="not-found-card__actions">
            <button
              className="button button--primary"
              onClick={() => setLoadAttempt((attempt) => attempt + 1)}
              type="button"
            >
              Try again
            </button>
            <Link className="button button--secondary" to="/">
              Back to my projects
            </Link>
          </div>
        </section>
      </main>
    );
  }

  if (recovery.kind === "corrupt") {
    return (
      <main className="not-found-screen">
        <Brand compact />
        <section className="not-found-card">
          <h1>Drawing needs help</h1>
          <RecoveryNotice kind="corrupt" />
          <Link className="button button--primary" to="/">
            Back to my projects
          </Link>
        </section>
      </main>
    );
  }

  const settings: DrawingSettings = {
    tool,
    colour,
    size,
    opacity,
    pressureEnabled: project.settings.pressureEnabled,
  };
  const effectiveStatus = saveStatus === "saved" && !isOnline ? "offline" : saveStatus;
  const comicPanels = recovery.documentState.panels;
  const dialogueLayerId = project.layers.find((layer) => layer.type === "object")?.id;
  const currentLayerState = layerState ?? {
    activeLayerId: recovery.documentState.activeLayerId,
    layers: recovery.documentState.layers,
  };
  const selectionAvailable = currentLayerState.layers.some(
    (layer) => layer.type === "raster" && layer.visible && !layer.locked,
  );

  const performLayerAction = (presetOrAction: LayerPreset | (() => boolean)): void => {
    if (!canAtActionBoundary("useFullTools")) {
      setLayerError("Ask a parent to use Layers.");
      return;
    }
    const changed =
      typeof presetOrAction === "function"
        ? presetOrAction()
        : (stageRef.current?.addLayer(presetOrAction) ?? false);
    setLayerError(changed ? "" : "That layer action is not available right now.");
  };

  const requestDestructiveLayerAction = (
    kind: "delete" | "clear" | "merge",
    layerId: string,
  ): void => {
    if (!canAtActionBoundary("useFullTools")) {
      setLayerError("Ask a parent to use Layers.");
      return;
    }
    if (!canDestructiveEdit) {
      setLayerError("Ask a parent before using that editing action.");
      return;
    }
    setPendingLayerAction({ kind, layerId });
  };

  const chooseStudioTool = (nextTool: StudioTool): void => {
    if (
      !isStudioToolAllowed(nextTool, parent.toolLevel) ||
      (isStudioToolAllowed(nextTool, "full") &&
        !isStudioToolAllowed(nextTool, "creative") &&
        !canAtActionBoundary("useFullTools"))
    ) {
      parent.askParent("Ask a parent to use that Studio tool.");
      return;
    }
    setStudioTool(nextTool);
  };

  return (
    <main className="studio-screen">
      <header className="studio-header">
        <div className="studio-header__left">
          <button
            className="icon-button"
            disabled={isNavigating || !isStageReady}
            onClick={() => void handleHome()}
            type="button"
            aria-label="Save and return to project library"
          >
            ←
          </button>
          <Brand compact linked={false} />
          <span className="studio-header__divider" aria-hidden="true" />
          <div className="studio-title">
            <h1>{project.name}</h1>
            <span>{project.type === "comic" ? "Comic" : "Drawing"}</span>
          </div>
        </div>
        <div className="studio-header__actions">
          <HistoryControls
            canRedo={history.canRedo}
            canUndo={history.canUndo}
            onRedo={redo}
            onUndo={undo}
          />
          <ManualSaveButton
            isSaving={effectiveStatus === "saving"}
            onSave={() => void handleSave()}
          />
          <button
            className="studio-action-button"
            disabled={!canExportBackup || isExporting || effectiveStatus === "saving"}
            onClick={() => void handleExport()}
            type="button"
            aria-label="Export project backup"
          >
            <span aria-hidden="true">⇩</span>
            {isExporting ? "Exporting" : "Backup"}
          </button>
        </div>
        <div className="studio-header__right">
          <SaveIndicator status={effectiveStatus} />
          <span className="zoom-pill" aria-label="Canvas zoom" aria-live="polite">
            {zoomPercent}%
          </span>
        </div>
      </header>

      <div className="studio-messages">
        {parent.notice ? (
          <div className="activity-route-notice" role="status">
            {parent.notice}
          </div>
        ) : null}
        {activityRoute.kind === "invalid" ? (
          <div className="activity-route-notice" role="status">
            {activityRoute.notice}
          </div>
        ) : null}
        {recovery.kind === "recovered" ? <RecoveryNotice kind="recovered" /> : null}
        {history.wasTrimmed ? (
          <div className="history-limit-notice" role="status">
            Older undo steps were safely folded into the drawing to save tablet memory.
          </div>
        ) : null}
        {saveError ? (
          <div className="studio-save-error" role="alert">
            {saveError}
          </div>
        ) : null}
        {routedActivity && (routedActivity.type === "challenge" || activeGuideSession) ? (
          <ActivityPanel
            activity={routedActivity}
            guideState={activeGuideSession ?? undefined}
            onExit={exitActivity}
            onNext={activityNext}
            onPrevious={activityPrevious}
            onRestart={restartActivity}
            onToggleGuide={toggleGuide}
          />
        ) : null}
      </div>

      <section
        className={`studio-workspace${parent.toolLevel === "full" ? " studio-workspace--layers" : ""}`}
        aria-label="Drawing studio"
      >
        <div className="studio-canvas-area">
          <CanvasStage
            dialogueLayerId={dialogueLayerId}
            documentSize={{ width: project.width, height: project.height }}
            initialArtworkBlob={recovery.blob}
            initialRasterLayers={recovery.rasterLayers}
            initialDocumentState={recovery.documentState}
            guideOverlay={guideOverlay}
            onComicSelectionChange={setComicSelection}
            onRasterSelectionChange={setRasterSelection}
            onComicTextEditRequest={openTextEditor}
            onDocumentChange={handleDocumentChange}
            onHistoryChange={setHistory}
            onReady={handleCanvasReady}
            onRestoreError={() => setError("We couldn’t restore this drawing safely.")}
            onLayerChange={handleLayerChange}
            onInteractionError={setLayerError}
            onColourPick={setColour}
            onZoomChange={setZoomPercent}
            ref={stageRef}
            settings={settings}
            shapeMode={shapeMode}
            fillTolerance={fillTolerance}
            studioTool={studioTool}
          />
        </div>
        {parent.toolLevel === "full" ? (
          <LayersPanel
            destructiveAllowed={canDestructiveEdit}
            onActivate={(layerId) => {
              if (canAtActionBoundary("useFullTools")) stageRef.current?.activateLayer(layerId);
            }}
            onAdd={(preset) => performLayerAction(preset)}
            onClear={(layerId) => requestDestructiveLayerAction("clear", layerId)}
            onDelete={(layerId) => requestDestructiveLayerAction("delete", layerId)}
            onLocked={(layerId, locked) =>
              performLayerAction(() => stageRef.current?.setLayerLocked(layerId, locked) ?? false)
            }
            onMerge={(layerId) => requestDestructiveLayerAction("merge", layerId)}
            onMove={(layerId, direction) =>
              performLayerAction(() => stageRef.current?.moveLayer(layerId, direction) ?? false)
            }
            onOpacity={(layerId, value) =>
              performLayerAction(() => stageRef.current?.setLayerOpacity(layerId, value) ?? false)
            }
            onRename={(layerId, name) =>
              performLayerAction(() => stageRef.current?.renameLayer(layerId, name) ?? false)
            }
            onVisible={(layerId, visible) =>
              performLayerAction(() => stageRef.current?.setLayerVisible(layerId, visible) ?? false)
            }
            state={currentLayerState}
            previewUrls={layerPreviewUrls}
          />
        ) : null}
      </section>

      {project.type === "comic" && parent.toolLevel === "full" ? (
        <>
          <ComicToolbar
            destructiveAllowed={canDestructiveEdit}
            onClearPanel={() => {
              if (!canAtActionBoundary("useFullTools")) {
                setLayerError("Ask a parent to edit comics.");
              } else if (canDestructiveEdit) setShowClearPanelDialog(true);
              else setLayerError("Ask a parent before clearing a comic panel.");
            }}
            onDuplicatePanel={() => {
              if (!canAtActionBoundary("useFullTools")) {
                setLayerError("Ask a parent to edit comics.");
                return;
              }
              const firstTarget = comicPanels.find((panel) => panel.id !== comicSelection.panelId);
              setDuplicateTargetId(firstTarget?.id ?? "");
              setShowDuplicatePanelDialog(true);
            }}
            onPanelChange={(panelId) => {
              if (canAtActionBoundary("useFullTools")) stageRef.current?.selectPanel(panelId);
            }}
            onToolChange={chooseStudioTool}
            panels={comicPanels}
            selectedPanelId={comicSelection.panelId}
            tool={studioTool}
          />
          {comicSelection.object ? (
            <BubbleInspector
              destructiveAllowed={canDestructiveEdit}
              object={comicSelection.object}
              onDelete={() => {
                if (!canAtActionBoundary("useFullTools")) {
                  setLayerError("Ask a parent to edit comics.");
                } else if (canDestructiveEdit) stageRef.current?.deleteSelectedComicObject();
                else setLayerError("Ask a parent before deleting dialogue.");
              }}
              onEdit={() => openTextEditor(comicSelection.object!)}
              onTransform={(change) => {
                if (canAtActionBoundary("useFullTools")) {
                  stageRef.current?.transformSelectedComicObject(change);
                }
              }}
            />
          ) : null}
        </>
      ) : null}

      {parent.toolLevel === "full" &&
      studioTool === "selectRasterArt" &&
      rasterSelection.hasSelection ? (
        <SelectionInspector
          destructiveAllowed={canDestructiveEdit}
          onApply={() => {
            if (canAtActionBoundary("useFullTools")) stageRef.current?.applyRasterSelection();
          }}
          onDelete={() => {
            if (!canAtActionBoundary("useFullTools")) {
              setLayerError("Ask a parent to select art.");
            } else if (canDestructiveEdit) stageRef.current?.deleteRasterSelection();
            else setLayerError("Ask a parent before deleting selected art.");
          }}
          onDeselect={() => {
            if (canAtActionBoundary("useFullTools")) stageRef.current?.clearRasterSelection();
          }}
          onDuplicate={() => {
            if (canAtActionBoundary("useFullTools")) stageRef.current?.duplicateRasterSelection();
          }}
          onFlipHorizontal={() => {
            if (canAtActionBoundary("useFullTools")) {
              stageRef.current?.flipRasterSelection("horizontal");
            }
          }}
          onFlipVertical={() => {
            if (canAtActionBoundary("useFullTools")) {
              stageRef.current?.flipRasterSelection("vertical");
            }
          }}
          selection={rasterSelection}
        />
      ) : null}

      <ToolDock
        active={isDrawingStudioTool(studioTool)}
        colour={colour}
        onColourChange={setColour}
        onFit={() => stageRef.current?.fitToView()}
        onOpacityChange={setOpacity}
        onFillToleranceChange={setFillTolerance}
        onReset={() => stageRef.current?.resetView()}
        onSelectArt={() => chooseStudioTool("selectRasterArt")}
        onSizeChange={setSize}
        onShapeModeChange={setShapeMode}
        onStudioToolChange={chooseStudioTool}
        onToolChange={(nextTool) => {
          setTool(nextTool);
          setStudioTool(nextTool);
        }}
        opacity={opacity}
        fillTolerance={fillTolerance}
        selectionActive={studioTool === "selectRasterArt"}
        selectionAvailable={selectionAvailable}
        size={size}
        shapeMode={shapeMode}
        studioTool={studioTool}
        tool={tool}
        toolLevel={parent.toolLevel}
      />

      {layerError ? (
        <div className="layer-error" role="alert">
          {layerError}
        </div>
      ) : null}

      {showLeaveDialog ? (
        <LeaveWithoutSavingDialog
          isRetrying={isNavigating}
          onLeave={() => {
            coordinatorRef.current?.dispose();
            void navigate("/");
          }}
          onRetry={() => void handleHome()}
          onStay={() => setShowLeaveDialog(false)}
        />
      ) : null}

      {showTextEditor ? (
        <BubbleTextDialog
          onCancel={() => {
            closeTextEditor();
          }}
          onChange={(value) => {
            if (!canAtActionBoundary("useFullTools")) return;
            textDraftRef.current = value;
            setTextDraft(value);
          }}
          onConfirm={commitTextEditor}
          text={textDraft}
        />
      ) : null}

      {showClearPanelDialog ? (
        <ComicActionDialog
          confirmLabel="Clear this panel"
          description="This removes the drawing and dialogue inside the selected panel. You can Undo it."
          onCancel={() => setShowClearPanelDialog(false)}
          onConfirm={() => {
            if (!canAtActionBoundary("useFullTools")) {
              setLayerError("Ask a parent to edit comics.");
            } else if (canDestructiveEdit) stageRef.current?.clearSelectedPanel();
            else setLayerError("Ask a parent before clearing a comic panel.");
            setShowClearPanelDialog(false);
          }}
          title="Clear selected panel?"
        />
      ) : null}

      {showDuplicatePanelDialog ? (
        <ComicActionDialog
          confirmLabel="Replace and duplicate"
          description="Choose a destination panel. Its drawing and dialogue will be replaced, and you can Undo it."
          onCancel={() => setShowDuplicatePanelDialog(false)}
          onConfirm={() => {
            if (canAtActionBoundary("useFullTools") && duplicateTargetId) {
              stageRef.current?.duplicateSelectedPanel(duplicateTargetId);
            }
            setShowDuplicatePanelDialog(false);
          }}
          title="Duplicate selected panel"
        >
          <label htmlFor="duplicate-panel-target">Copy into</label>
          <select
            id="duplicate-panel-target"
            onChange={(event) => setDuplicateTargetId(event.target.value)}
            value={duplicateTargetId}
          >
            {comicPanels
              .filter((panel) => panel.id !== comicSelection.panelId)
              .map((panel) => (
                <option key={panel.id} value={panel.id}>
                  Panel {panel.order + 1}
                </option>
              ))}
          </select>
        </ComicActionDialog>
      ) : null}

      {pendingLayerAction ? (
        <ComicActionDialog
          confirmLabel={
            pendingLayerAction.kind === "merge"
              ? "Merge layers"
              : pendingLayerAction.kind === "delete"
                ? "Delete layer"
                : "Clear layer"
          }
          description={
            pendingLayerAction.kind === "merge"
              ? "This permanently combines the selected drawing layer with the one below. You can Undo it."
              : pendingLayerAction.kind === "delete"
                ? "This removes the layer and its drawing. You can Undo it."
                : "This removes every mark on this drawing layer. You can Undo it."
          }
          onCancel={() => setPendingLayerAction(null)}
          onConfirm={() => {
            const action = pendingLayerAction;
            if (!canAtActionBoundary("useFullTools") || !canDestructiveEdit) {
              setLayerError("Ask a parent before using that editing action.");
              setPendingLayerAction(null);
              return;
            }
            performLayerAction(() => {
              if (action.kind === "delete")
                return stageRef.current?.deleteLayer(action.layerId) ?? false;
              if (action.kind === "merge")
                return stageRef.current?.mergeLayerDown(action.layerId) ?? false;
              return stageRef.current?.clearLayer(action.layerId) ?? false;
            });
            setPendingLayerAction(null);
          }}
          title={`${pendingLayerAction.kind === "merge" ? "Merge" : pendingLayerAction.kind === "delete" ? "Delete" : "Clear"} this layer?`}
        />
      ) : null}
    </main>
  );
}
